// HTTP transport with single-flight token refresh + 401 handling, shared by
// admin and liff. The per-app `api` endpoint surfaces and any extra session
// helpers stay in each app; this owns only fetchApi/refresh/onSessionExpired.
// Apps differ only in: which token store backs it, and the two error messages
// (i18n key differs) — both injected via config.

import type { TokenData } from "../auth/oauth-core";

export interface ApiTransportConfig {
  /** Path prefix for every request. Defaults to "/api". */
  baseUrl?: string;
  getAccessToken: () => string | null;
  getTokens: () => TokenData | null;
  saveTokens: (tokens: TokenData) => void;
  getClientType: () => string;
  /** Message thrown when the session is unrecoverably expired (401 after refresh). */
  sessionExpiredMessage: () => string;
  /** Message for a non-401 error response with no body.message. */
  apiErrorMessage: (status: number) => string;
  /** Per-request abort timeout, in ms. Defaults to 15000. */
  requestTimeoutMs?: number;
  /** Message for a request aborted by the timeout above before any response
   *  arrived. Optional — falls back to an untranslated string for a caller
   *  (e.g. admin) that does not supply one. */
  timeoutMessage?: () => string;
  /** Message for a request that failed before reaching the server: the browser
   *  reports itself offline, or fetch threw (DNS/connection refused/CORS —
   *  browsers surface all of these as a bare TypeError). Optional, same
   *  fallback as timeoutMessage. */
  offlineMessage?: () => string;
}

/** A binary response plus the filename the server suggested in Content-Disposition. */
export interface FetchedFile {
  blob: Blob;
  filename: string | null;
}

export interface ApiTransport {
  fetchApi<T>(path: string, options?: RequestInit): Promise<T>;
  /** Same auth/refresh path as fetchApi, for endpoints that return bytes, not JSON. */
  fetchFile(path: string, options?: RequestInit): Promise<FetchedFile>;
  refreshAccessToken(): Promise<boolean>;
  setOnSessionExpired(cb: () => void): void;
}

/** RFC 5987 `filename*` first (it carries the charset, so it survives non-ASCII), then plain
 *  `filename`. Mail attachments routinely arrive with Japanese names, which the plain form
 *  mangles. */
function filenameFromDisposition(disposition: string | null): string | null {
  if (!disposition) return null;
  const extended = /filename\*=(?:UTF-8|utf-8)''([^;]+)/.exec(disposition);
  if (extended) {
    try {
      return decodeURIComponent(extended[1]);
    } catch {
      /* fall through to the plain form */
    }
  }
  const plain = /filename="?([^";]+)"?/.exec(disposition);
  return plain ? plain[1] : null;
}

export function createApiTransport(config: ApiTransportConfig): ApiTransport {
  const {
    baseUrl = "/api",
    getAccessToken,
    getTokens,
    saveTokens,
    getClientType,
    sessionExpiredMessage,
    apiErrorMessage,
    requestTimeoutMs = 15000,
    timeoutMessage = () => "The request timed out. Please try again.",
    offlineMessage = () => "You appear to be offline. Check your connection and try again.",
  } = config;

  let refreshPromise: Promise<boolean> | null = null;
  let onSessionExpired: (() => void) | null = null;

  /**
   * AbortSignal.timeout is Safari/iOS 16+; an older LINE webview has no such
   * method, and calling it throws a TypeError before the fetch even starts —
   * which the classifier below can't tell apart from a real network failure,
   * so every request would misreport as offline. Fall back to a plain
   * AbortController, aborted by setTimeout; `cancel()` clears that timer once
   * the fetch this signal was for has settled, so it can't fire late and abort
   * something else.
   */
  function timeoutSignal(ms: number): { signal: AbortSignal; cancel: () => void } {
    if (typeof AbortSignal.timeout === "function") {
      return { signal: AbortSignal.timeout(ms), cancel: () => {} };
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ms);
    return { signal: controller.signal, cancel: () => clearTimeout(timer) };
  }

  /**
   * fetch with a per-request abort timeout and network-failure classification,
   * shared by every call site (authedFetch's two attempts, and refresh below)
   * so none of them can hang: inside the LINE webview there is no reload
   * gesture, so a request that never settles is a dead end, not just a wait.
   */
  async function timedFetch(
    path: string,
    options: RequestInit | undefined,
    headers?: Record<string, string>,
  ): Promise<Response> {
    const owned = !options?.signal;
    const { signal, cancel } = owned ? timeoutSignal(requestTimeoutMs) : { signal: options!.signal!, cancel: () => {} };
    try {
      return await fetch(`${baseUrl}${path}`, {
        ...options,
        ...(headers ? { headers } : {}),
        signal,
      });
    } catch (e) {
      // AbortSignal.timeout rejects with a "TimeoutError" DOMException; a signal
      // aborted some other way (a caller's own, none exist yet) is "AbortError" —
      // both mean no response arrived, so both read as a timeout to the user.
      if (e instanceof DOMException && (e.name === "TimeoutError" || e.name === "AbortError")) {
        throw new Error(timeoutMessage());
      }
      // A browser fetch that never reaches the server (DNS, connection refused,
      // CORS) throws a bare TypeError, with no way to tell those apart from here —
      // so "offline" is the closest honest message, cross-checked against
      // navigator.onLine where the browser can confirm it directly.
      if ((typeof navigator !== "undefined" && navigator.onLine === false) || e instanceof TypeError) {
        throw new Error(offlineMessage());
      }
      throw e;
    } finally {
      cancel();
    }
  }

  function setOnSessionExpired(cb: () => void) {
    onSessionExpired = cb;
  }

  function refreshAccessToken(): Promise<boolean> {
    if (refreshPromise) return refreshPromise;

    let cancelTimeout = () => {};
    refreshPromise = (async () => {
      try {
        const tokens = getTokens();
        if (!tokens?.refresh_token) return false;

        // Errors here are already swallowed into `return false` below, so this
        // needs only the timeout itself, not timedFetch's message mapping.
        const { signal, cancel } = timeoutSignal(requestTimeoutMs);
        cancelTimeout = cancel;
        const res = await fetch(`${baseUrl}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken: tokens.refresh_token, clientType: getClientType() }),
          signal,
        });

        if (!res.ok) return false;

        const newTokens = await res.json();
        // Merge, never overwrite. The refresh grant is not obliged to return an
        // id_token, and when it did not, replacing the bundle wholesale DELETED
        // the one saved at login -- 15 minutes in, on the default access-token
        // TTL. Sign-out then had no id_token_hint for /end_session and fell
        // through to its confirmation page on every click. Fields the response
        // does carry still win; ones it omits keep what login stored.
        saveTokens({ ...tokens, ...newTokens });
        return true;
      } catch {
        return false;
      } finally {
        cancelTimeout();
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  }

  /**
   * The authenticated request itself: bearer header, one silent refresh-and-retry on
   * 401, and the two error shapes below. Shared so fetchApi and fetchFile cannot drift
   * on the part that matters — a second hand-rolled fetch is how a caller ends up
   * without refresh handling, or authenticating some other way entirely.
   */
  async function authedFetch(
    path: string,
    options: RequestInit | undefined,
    sendJsonContentType: boolean,
  ): Promise<Response> {
    let token = getAccessToken();
    const headers: Record<string, string> = {
      ...(sendJsonContentType ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    let res = await timedFetch(path, options, headers);

    if (res.status === 401 && token) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        token = getAccessToken();
        headers.Authorization = `Bearer ${token}`;
        res = await timedFetch(path, options, headers);
      }
    }

    if (res.status === 401) {
      onSessionExpired?.();
      // Carries `status` like every other failure below: this is the one branch that used
      // to throw a bare Error, which left callers matching the message prose — per locale,
      // hardcoded twice each — to notice a session had expired.
      const err = new Error(sessionExpiredMessage());
      (err as Error & { status?: number }).status = 401;
      throw err;
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.message || apiErrorMessage(res.status));
      // Surface the HTTP status and any stable server error code so callers can
      // branch on the error type (e.g. "SLOT_UNAVAILABLE") instead of matching
      // language-dependent message strings.
      (err as Error & { status?: number; code?: string }).status = res.status;
      (err as Error & { status?: number; code?: string }).code = body.code;
      throw err;
    }

    return res;
  }

  async function fetchApi<T>(path: string, options?: RequestInit): Promise<T> {
    const res = await authedFetch(path, options, true);
    return res.json();
  }

  /**
   * For endpoints that return a file. Exists so a download can be authenticated by the
   * Authorization header like everything else: the alternative — an <a href> the browser
   * navigates to — cannot set headers, and the credential ends up in the URL, in browser
   * history, and in every log along the way. Callers turn the blob into an object URL.
   *
   * No Content-Type is sent (there is no request body), and the response is read whole
   * rather than streamed. That is fine here and deliberate: the server already buffers
   * these in full before sending.
   */
  async function fetchFile(path: string, options?: RequestInit): Promise<FetchedFile> {
    const res = await authedFetch(path, options, false);
    return {
      blob: await res.blob(),
      filename: filenameFromDisposition(res.headers.get("Content-Disposition")),
    };
  }

  return { fetchApi, fetchFile, refreshAccessToken, setOnSessionExpired };
}
